import { css } from '@emotion/react';
import { ScreenReaderOnly, semanticTokens, Tab as TabComponent, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import Skeleton from 'react-loading-skeleton';
import { BOUND_SIDE, formatCurrency, getCurrencyByPos, Tab } from 'shared';

const styles = {
  dateTab: (isActive: boolean) => css`
    display: flex;
    flex-direction: column;
    border: none;
    background: transparent;
    font-size: ${isActive ? tokens.fontSize.paragraph['paragraph-3'] : tokens.fontSize.paragraph['paragraph-2']};
    font-weight: ${isActive ? '700' : '400'};
    color: ${isActive ? semanticTokens.color.text.primary : semanticTokens.color.text.secondary};

    > p {
      margin: 0;
    }
  `,
  secondaryTextStyle: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.primary};
    font-weight: 700;
  `,
};

type Props = {
  tab: Tab;
  boundSide: BOUND_SIDE;
  onClick: (date: DateTime) => void;
  pos: string;
  activeTab: string | undefined;
  isLoading?: boolean;
};

export const DateTab = ({ tab, boundSide, onClick, pos, activeTab, isLoading }: Props) => {
  const { price, date } = tab;
  const isActiveTab = date.toFormat('yyyy-MM-dd') === activeTab;
  const formattedPrice = price
    ? formatCurrency(price, pos, {
        displayLocalFormats: true,
        forceMinPrecision: true,
        enforcedLocale: true,
      })
    : '';
  const { CURRENCY_CODE } = getCurrencyByPos(pos);
  const priceSR = !price ? t('a11y.priceUnavailable', { defaultValue: 'price unavailable' }) : price;

  return (
    <TabComponent.Trigger
      value={date.toFormat('yyyy-MM-dd')}
      onClick={() => onClick(date)}
      data-testid={`upsell-date-tab-day-${date.toFormat('yyyy-MM-dd')}-${boundSide}`}
    >
      <div css={styles.dateTab(isActiveTab)}>
        <p>{date.toLocaleString({ weekday: 'short', day: 'numeric', month: 'short' })}</p>

        <ScreenReaderOnly as="span">
          {price ? t('common:from') : ''} {priceSR} {price ? CURRENCY_CODE : ''}
        </ScreenReaderOnly>
        <div css={styles.secondaryTextStyle}>
          {isLoading ? <Skeleton height={10} width={40} /> : <span aria-hidden="true">{formattedPrice}</span>}
        </div>
      </div>
    </TabComponent.Trigger>
  );
};
